import { motion } from 'framer-motion';
import { FaArrowRight, FaWhatsapp, FaCheckCircle } from 'react-icons/fa';
import Container from '../components/ui/Container';
import SectionHeading from '../components/ui/SectionHeading';
import ServiceCard from '../components/ui/ServiceCard';
import TestimonialCard from '../components/ui/TestimonialCard';
import FAQAccordion from '../components/ui/FAQAccordion';
import CTASection from '../components/ui/CTASection';
import ContactForm from '../components/ui/ContactForm';
import Button from '../components/ui/Button';
import IndustriesGrid from '../components/ui/IndustriesGrid';
import { featuredServices } from '../data/services';
import { testimonials, faqs, stats, industries, processSteps, whyChooseUs } from '../data/content';
import { siteConfig } from '../data/config';

const { contact: contactInfo } = siteConfig;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export default function HomePage() {
  return (
    <main className="w-full overflow-x-hidden">
      {/* ─── HERO ─── */}
      <section
        className="relative w-full min-h-screen flex items-center pt-28 pb-40 md:pt-32 md:pb-44 overflow-hidden"
        style={{ background: 'var(--gradient-hero)' }}
      >
        {/* decorative circles */}
        <div className="absolute top-20 right-0 w-96 h-96 rounded-full opacity-10" style={{ background: 'var(--color-secondary)', filter: 'blur(80px)' }} />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-10" style={{ background: 'var(--color-accent)', filter: 'blur(60px)' }} />

        <Container className="relative z-10">
          <div className="max-w-7xl">
            <Container className="relative z-10">
              <div className="max-w-6xl">
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="font-bold text-white mb-6 leading-tight text-balance
             text-4xl
             sm:text-5xl
             md:text-5xl
             lg:text-5xl
             xl:text-6xl
             2xl:text-7xl"
                >
                  Your Trusted Partner in{" "}
                  <span
                    style={{
                      background: "var(--gradient-gold)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Finance, Legal
                  </span>{" "}
                  &amp; Tech Solutions
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="mb-8 md:mb-10 max-w-7xl leading-relaxed
             text-lg
             md:text-xl
             lg:text-xl
             xl:text-2xl"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  From GST registration and company formation to trademark
                  protection and website development — we handle all your
                  compliance, legal, and technology needs under one roof.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex flex-wrap gap-4 mb-12"
                >
                  <Button href="/contact" variant="primary" size="lg">
                    Get Free Consultation <FaArrowRight />
                  </Button>

                  <Button
                    href={`https://wa.me/${contactInfo.whatsapp}`}
                    variant="ghost"
                    size="lg"
                  >
                    <FaWhatsapp className="text-xl text-green-400" />
                    WhatsApp Us
                  </Button>
                </motion.div>

                {/* Trust Badges */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-wrap gap-4 lg:gap-6"
                >
                  {[
                    "ISO Certified Process",
                    "100% Online Service",
                    "Expert Finance & Legal Team",
                    "Pan-India Service",
                  ].map((t) => (
                    <div key={t} className="flex items-center gap-2">
                      <FaCheckCircle
                        style={{ color: "var(--color-secondary)" }}
                      />
                      <span className="text-sm xl:text-base text-white/80">
                        {t}
                      </span>
                    </div>
                  ))}
                </motion.div>
              </div>
            </Container>

          </div>
        </Container>

        {/* stats bar */}
        <div className="absolute bottom-0 left-0 right-0 w-full" style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(10px)' }}>
          <Container>
            <div className="grid grid-cols-2 md:grid-cols-4 py-6 lg:py-8 gap-4 lg:gap-6">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + i * 0.1 }}
                  className="text-center"
                >
                  <div className="text-2xl md:text-3xl lg:text-4xl font-bold" style={{ color: 'var(--color-secondary-light)' }}>{s.value}</div>
                  <div className="text-xs lg:text-sm text-white/70 mt-1">{s.label}</div>
                </motion.div>
              ))}
            </div>
          </Container>
        </div>
      </section>

      {/* ─── ABOUT SNIPPET ─── */}
      <section className="section-pad" style={{ background: 'var(--color-surface)' }}>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: 'var(--gradient-gold)', color: 'var(--color-primary-dark)' }}>
                About Finegal Tech Solution
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5" style={{ color: 'var(--color-text)' }}>
                One-Stop Solution for All Your Business Needs
              </h2>
              <p className="text-base lg:text-lg mb-4" style={{ color: 'var(--color-text-muted)' }}>
                Finegal Tech Solution is a premier multi-disciplinary consultancy combining expertise in finance, legal, compliance, and technology. With over 10 years of experience and 5000+ satisfied clients, we are the trusted partner for businesses at every stage of growth.
              </p>
              <p className="text-base lg:text-lg mb-6" style={{ color: 'var(--color-text-muted)' }}>
                Our team of qualified Finance Experts, Company Secretaries, Legal Experts, and Technology Professionals work together to deliver seamless, end-to-end solutions tailored to your business needs.
              </p>
              <Button href="/about" variant="dark">
                Learn More About Us <FaArrowRight />
              </Button>
            </motion.div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '10+', label: 'Years of Experience', icon: '🏆' },
                { value: '5000+', label: 'Happy Clients', icon: '😊' },
                { value: '200+', label: 'Services Offered', icon: '⚡' },
                { value: '99%', label: 'Success Rate', icon: '✅' },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="card p-6 text-center"
                >
                  <div className="text-8xl mb-2">{item.icon}</div>
                  <div className="text-3xl font-bold mb-1" style={{ color: 'var(--color-primary)' }}>{item.value}</div>
                  <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{item.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ─── FEATURED SERVICES ─── */}
      <section className="section-pad section-bg-alt">
        <Container>
          <SectionHeading
            badge="What We Offer"
            title="Our Featured Services"
            subtitle="Comprehensive solutions across Finance, Legal, Compliance, and Technology for businesses of all sizes."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredServices.map((svc, i) => (
              <ServiceCard
                key={svc.slug}
                title={svc.title}
                description={`Expert ${svc.title.toLowerCase()} services with fast turnaround and complete compliance support.`}
                slug={svc.slug}
                category={svc.category}
                index={i}
              />
            ))}
          </div>
          <div className="text-center mt-10">
            <Button href="/services" variant="dark" size="lg">
              View All Services <FaArrowRight />
            </Button>
          </div>
        </Container>
      </section>

      {/* ─── WHY CHOOSE US ─── */}
      <section className="section-pad" style={{ background: 'var(--color-surface)' }}>
        <Container>
          <SectionHeading
            badge="Why Choose Us"
            title="The Finegal Advantage"
            subtitle="We combine expertise, technology, and dedication to deliver outstanding results for every client."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card p-6 flex gap-4"
              >
                <span className="text-7xl flex-shrink-0">{item.icon}</span>
                <div>
                  <h3 className="font-bold mb-2 text-sm md:text-lg" style={{ color: 'var(--color-text)' }}>{item.title}</h3>
                  <p className="text-sm md:text-base" style={{ color: 'var(--color-text-muted)' }}>{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* ─── PROCESS FLOW ─── */}
      <section className="section-pad section-bg-alt">
        <Container>
          <SectionHeading
            badge="How We Work"
            title="Our Simple 5-Step Process"
            subtitle="A streamlined process designed to make your compliance journey effortless."
          />
          <div className="relative">
            {/* connector line */}
            <div className="hidden lg:block absolute top-12 left-0 right-0 h-0.5" style={{ background: 'var(--color-border)', margin: '0 8%' }} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {processSteps.map((step, i) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center relative"
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 relative z-10"
                    style={{ background: 'var(--gradient-primary)', color: '#fff', boxShadow: 'var(--shadow-md)' }}
                  >
                    {step.step}
                  </div>
                  <h3 className="font-bold mb-2 text-sm md:text-base" style={{ color: 'var(--color-text)' }}>{step.title}</h3>
                  <p className="text-xs md:text-sm" style={{ color: 'var(--color-text-muted)' }}>{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ─── INDUSTRIES ─── */}
      <section className="section-pad" style={{ background: 'var(--color-surface)' }}>
        <Container>
          <SectionHeading
            badge="Industries We Serve"
            title="Serving Businesses Across All Sectors"
            subtitle="Our expertise spans across diverse industries, providing specialized compliance and advisory solutions."
          />
          <IndustriesGrid industries={industries} />
        </Container>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="section-pad section-bg-alt">
        <Container>
          <SectionHeading
            badge="Client Stories"
            title="What Our Clients Say"
            subtitle="5000+ businesses trust Finegal Tech Solution for their compliance and growth needs."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <TestimonialCard key={t.id} {...t} index={i} />
            ))}
          </div>
        </Container>
      </section>

      {/* ─── FAQ + CONTACT ─── */}
      <section className="section-pad" style={{ background: 'var(--color-surface)' }}>
        <Container>
          <SectionHeading
            badge="Get In Touch"
            title="Request a Free Consultation"
            subtitle="Fill in the form and our experts will reach out to you within 24 hours."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left – Contact Form */}
            <div className="card p-8">
              <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--color-text)' }}>Send us a Message</h3>
              <p className="text-sm mb-6" style={{ color: 'var(--color-text-muted)' }}>We reply within 24 hours — or chat instantly on WhatsApp.</p>
              <ContactForm />
            </div>
            {/* Right – FAQ */}
            <div>
              <FAQAccordion items={faqs} />
            </div>
          </div>
        </Container>
      </section>



      {/* ─── CTA ─── */}
      <CTASection
        title="Ready to Simplify Your Compliance?"
        subtitle="Join 5000+ businesses that trust Finegal Tech Solution for their Finance, Legal, and Technology needs."
        primaryLabel="Get Free Consultation"
        primaryHref="/contact"
        secondaryLabel="Explore Services"
        secondaryHref="/services"
      />
    </main>
  );
}
